import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Quote } from "lucide-react";
import { GUIDES, AWARDS, CRITERIA, COVERAGE, REGIONS, avgScore, distinctionOf, DISTINCTIONS } from "../mock";
import { usePlaces } from "../context/PlacesContext";
import { SectionLabel, PlaceCard } from "../components/Bits";

export default function Home() {
  const { places, getPlace, geo, loading } = usePlaces();

  if (loading) {
    return <div className="pt-[120px] pb-40 text-center text-ink-soft font-serif text-2xl">Loading…</div>;
  }

  const featured = places.filter((p) => p.featured);
  const latest = places.slice(0, 6);
  const hero = getPlace("ox-and-angela") || places[0];
  const heroLevel = hero ? distinctionOf(hero) : 0;
  const spotlight = REGIONS.filter((r) => (geo.regionsByCountry[r.country] || []).includes(r.name)).slice(0, 6);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0 kenburns">
          <img src={hero?.image} alt="Featured restaurant" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/50" />
        <div className="absolute inset-0 grain" />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 w-full pb-16 pt-32">
          <div className="grid lg:grid-cols-[1.5fr_1fr] gap-12 items-end">
            <div className="rise-in">
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-8 bg-brass" />
                <span className="eyebrow text-brass">Canada · USA · Europe · Asia–Pacific</span>
              </div>
              <h1 className="font-serif text-paper text-4xl sm:text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.98] font-semibold display-balance">
                The guide to places <em className="italic text-brass">worth experiencing.</em>
              </h1>
              <p className="text-paper/75 text-lg mt-8 max-w-xl leading-relaxed">
                Placeworthy sends real reviewers to real places. We judge restaurants, hospitality
                venues, and local experiences against five honest criteria — and publish verdicts you can trust.
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-10">
                <a href="mailto:placeworthy@gmail.com" className="inline-flex items-center gap-2 px-7 py-4 bg-paper text-ink text-sm font-semibold tracking-wide hover:bg-brass hover:text-ink transition-colors">
                  Get Early Access <ArrowRight className="w-4 h-4" />
                </a>
                <Link to="/explore" className="inline-flex items-center gap-2 px-7 py-4 border border-paper/40 text-paper text-sm font-semibold tracking-wide hover:bg-paper/10 transition-colors">
                  Explore Places
                </Link>
              </div>
            </div>

            {hero && (
              <div className="rise-in" style={{ animationDelay: "180ms" }}>
                <div className="bg-paper p-6 shadow-2xl max-w-sm mx-auto lg:ml-auto lg:mr-0">
                  {heroLevel > 0 && (
                    <div className="flex flex-col items-center text-center mb-4">
                      <span className="flex items-center justify-center gap-1.5">
                        {Array.from({ length: heroLevel }).map((_, i) => (
                          <img key={i} src="/placeworthy-mark.png" alt="" aria-hidden="true" className="w-7 h-7" />
                        ))}
                      </span>
                      <span className="eyebrow text-[10px] text-claret mt-2">{DISTINCTIONS[heroLevel].label}</span>
                    </div>
                  )}
                  <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-center">{hero.name}</h3>
                  <p className="text-sm text-ink-soft text-center">{hero.neighborhood} · {hero.city}</p>
                  <div className="grid grid-cols-5 gap-2 mt-5 pt-5 border-t border-ink/10">
                    {CRITERIA.map((c) => (
                      <div key={c.key} className="text-center">
                        <div className="font-serif text-xl font-semibold">{hero.scores[c.key]}</div>
                        <div className="text-[9px] eyebrow text-ink-soft mt-1">{c.label.slice(0, 4)}</div>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-ink-soft mt-5 leading-relaxed">{hero.excerpt}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <SectionLabel>Featured this month</SectionLabel>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold mt-4 display-balance">
              Places we cannot stop talking about.
            </h2>
          </div>
          <Link to="/reviews" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold link-underline">
            All reviews <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {featured.slice(0, 4).map((p, i) => <PlaceCard key={p.id} place={p} index={i} />)}
        </div>
      </section>

      {/* HOW WE REVIEW */}
      <section className="bg-ink text-paper relative grain">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 relative">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-brass" />
              <span className="eyebrow text-brass">How we review</span>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-6xl font-semibold leading-[1.02] display-balance">
              Five criteria. No shortcuts.
            </h2>
            <p className="text-paper/60 text-lg mt-6 leading-relaxed">
              Every venue is judged against the same five standards, in person, every time.
              The criteria do not change. The score cannot be bought.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-paper/10 mt-16 border border-paper/10">
            {CRITERIA.map((c, i) => (
              <div key={c.key} className="bg-ink p-8 hover:bg-claret/20 transition-colors duration-500 group">
                <span className="font-serif text-5xl font-semibold text-paper/25 group-hover:text-brass transition-colors">0{i + 1}</span>
                <h3 className="font-serif text-2xl font-semibold mt-4">{c.label}</h3>
                <p className="text-paper/55 text-sm mt-3 leading-relaxed">{c.blurb}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LATEST REVIEWS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <SectionLabel>Latest reviews</SectionLabel>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold mt-4">Fresh verdicts.</h2>
          </div>
          <Link to="/reviews" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold link-underline">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
          {latest.map((p, i) => <PlaceCard key={p.id} place={p} index={i} />)}
        </div>
      </section>

      {/* DESTINATIONS */}
      <section className="bg-paper-dark relative grain">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 relative">
          <div className="flex items-end justify-between mb-12">
            <div>
              <SectionLabel>Explore by destination</SectionLabel>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold mt-4 display-balance">
                From Calgary to New York, and everywhere we grow next.
              </h2>
            </div>
            <Link to="/destinations" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold link-underline">
              All destinations <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {spotlight.map((r) => (
              <Link key={r.name} to={`/explore?country=${encodeURIComponent(r.country)}&region=${encodeURIComponent(r.name)}`} className="img-zoom group relative aspect-[3/4] bg-ink/10 overflow-hidden">
                <img src={r.image} alt={r.name} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-ink/45 group-hover:bg-ink/25 transition-colors" />
                <div className="absolute inset-0 flex flex-col justify-end p-4">
                  <span className="text-paper/70 eyebrow text-[9px]">{r.country}</span>
                  <span className="font-serif text-paper text-xl font-semibold leading-tight">{r.name}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
